import { useQuery } from "@tanstack/react-query";
import { AlertOctagon } from "lucide-react";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type { Priority, WorkItemSummary } from "../../shared/types/api";
import { groupByStatus, projectTally } from "./myWorkFilters";
import { WorkItemTypeBadge } from "./WorkItemTypeBadge";
import {
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  PRIORITY_TONE,
  STATUS_LABELS,
  formatHours,
  formatSlippage,
} from "./workItemVocabulary";

/**
 * One entry in the project filter.
 *
 * <p>The choices come from your own work, so the count means "how many of yours".
 */
interface ProjectChoice {
  id: string;
  key: string;
  name: string;
  count?: number;
}

/**
 * Everything open and assigned to the reader (§96), and a way out of it.
 *
 * <p>Grouped by status rather than sorted into one list, because the question this page answers
 * is "what should I pick up next", and that is a different question from "what do I own". Items
 * already in progress belong at the top; the backlog underneath is where the next one comes from.
 *
 * <p>Overdue items are marked rather than resorted. Moving them to the top would answer a
 * question nobody asked — the date matters, but so does what is already half-finished.
 *
 * <p>This page stays inside your own assignments. Looking for work that is not yours is what the
 * Search page is for; a second search box here only made the same list mean two different things.
 */
/* The parameters this screen owns; outside the component so useUrlFilters can memoise on it. */
const MY_WORK_FILTERS = { project: "", priority: "" };

export function MyWorkPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  /* In the URL, so Back from an item returns to the same project and priority. Absent means
     "all", which is why these read back as null rather than as an empty string. */
  const [filters, setFilters] = useUrlFilters(MY_WORK_FILTERS);
  const projectId = filters.project === "" ? null : filters.project;
  const priority = filters.priority === "" ? null : (filters.priority as Priority);
  const setProjectId = (next: string | null) => setFilters({ project: next ?? "" });
  const setPriority = (next: Priority | null) => setFilters({ priority: next ?? "" });

  const mine = useQuery({
    queryKey: ["my-work"],
    queryFn: () => api.get<WorkItemSummary[]>("/api/v1/work-items/my"),
  });

  if (mine.isPending) {
    return <LoadingState label="Loading your work…" />;
  }
  if (mine.isError) {
    return <ErrorState error={mine.error} onRetry={() => void mine.refetch()} />;
  }

  /* The choices are the projects your own work is in, with a count each — a shortlist rather
     than a directory. */
  const choices: ProjectChoice[] = projectTally(mine.data);
  const chosen = choices.find((project) => project.id === projectId) ?? null;

  /*
   * Filtered in the browser, unlike the project tables. Everything assigned to one person is
   * already in hand — this page fetches the whole list in one call — so narrowing it here is
   * exact, and asking the server again would be a round trip to re-sort what is on screen.
   */
  const inProject = projectId
    ? mine.data.filter((item) => item.projectId === projectId)
    : mine.data;
  const shown = priority ? inProject.filter((item) => item.priority === priority) : inProject;

  /* Only the priorities somebody actually has, worst first. A row of four chips where three read
     zero is a filter that mostly offers dead ends. */
  const priorityChoices = PRIORITY_ORDER.map((value) => ({
    value,
    count: inProject.filter((item) => item.priority === value).length,
  })).filter((choice) => choice.count > 0);

  const multipleProjects = choices.length > 1;
  const groups = groupByStatus(shown);
  const blocked = shown.filter((item) => item.openBlockerCount > 0).length;

  return (
    <>
      <PageHeader
        title="My work"
        subtitle={subtitle({
          shown: shown.length,
          mine: mine.data.length,
          filteredTo: chosen?.name,
          blocked,
        })}
      />

      {/* The filter sits on its own line, unboxed. A card around three chips is more chrome than
          content, and this row is often absent entirely. */}
      {multipleProjects && (
        <div className="-mt-2 mb-4 flex flex-wrap items-center gap-1.5">
          <ProjectChip
            label="All projects"
            count={mine.data.length}
            selected={projectId == null}
            onClick={() => {
              setProjectId(null);
              setPriority(null);
            }}
          />
          {choices.map((project) => (
            <ProjectChip
              key={project.id}
              label={project.key}
              title={project.name}
              count={project.count}
              selected={projectId === project.id}
              onClick={() => {
                setProjectId(project.id);
                /* The priorities on offer are the ones in the chosen project, so a choice made
                   against the old set may no longer be one of them — and a filter nothing can
                   match reads as an empty inbox rather than as a filter. */
                setPriority(null);
              }}
            />
          ))}
        </div>
      )}

      {priorityChoices.length > 1 && (
        <div className="-mt-2 mb-4 flex flex-wrap items-center gap-1.5">
          <ProjectChip
            label="Any priority"
            count={inProject.length}
            selected={priority == null}
            onClick={() => setPriority(null)}
          />
          {priorityChoices.map((choice) => (
            <ProjectChip
              key={choice.value}
              label={PRIORITY_LABELS[choice.value]}
              count={choice.count}
              selected={priority === choice.value}
              onClick={() => setPriority(choice.value)}
            />
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <Card>
          {chosen && mine.data.length > 0 ? (
            <EmptyState
              title={`Nothing assigned to you in ${chosen.name}`}
              message="You have open work elsewhere. Choose All projects to see it."
            />
          ) : (
            /* Also the case where a project is still selected but you have no work anywhere.
               Saying "none in this project" there would imply there is some in another. */
            <EmptyState
              title="Nothing assigned to you"
              message="Work shows up here when somebody makes you its assignee. §21 gives every item exactly one, so this is the list you are actually accountable for."
            />
          )}
        </Card>
      ) : (
        /*
          One surface for the whole list rather than a card per status. Three items used to
          occupy most of the screen because each status brought a bordered box and a heading
          block with it; the grouping is worth keeping, the packaging around it was not.
        */
        <Card className="overflow-hidden">
          {groups.map((group, index) => (
            <section key={group.status}>
              <div
                className={cn(
                  "flex items-baseline gap-2 px-5 pb-2 pt-4",
                  index > 0 && "border-t border-ink-100",
                )}
              >
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-500">
                  {STATUS_LABELS[group.status]}
                </h2>
                {/* ink-500, not 400: at 11px the lighter shade measures 3.12:1, and this count is
                      information rather than decoration. */}
                <span className="tnum text-[11px] text-ink-500">{group.items.length}</span>
              </div>

              <ul>
                {group.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/work-items/${item.itemKey}`)}
                      className="group flex w-full items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-ink-50"
                    >
                      <WorkItemTypeBadge type={item.workItemType} />

                      {/* The project is only worth naming when more than one is on screen. */}
                      {projectId == null && multipleProjects && (
                        <span
                          className="shrink-0 rounded bg-ink-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-ink-600"
                          title={item.projectName}
                        >
                          {item.projectKey}
                        </span>
                      )}

                      <span className="shrink-0 font-mono text-xs text-ink-500">
                        {item.itemKey}
                      </span>

                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
                        {item.title}
                      </span>

                      {item.openBlockerCount > 0 && (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
                          <AlertOctagon className="h-3 w-3" />
                          blocked
                        </span>
                      )}

                      {/*
                          The numbers travel together in a fixed cluster on the right. Spread
                          across the row they read as five separate columns to scan; gathered,
                          the eye follows one edge down the list.
                        */}
                      <span className="flex shrink-0 items-center gap-4 text-xs">
                        {/* Testing, not development: this row is on your list because someone
                            else's work is waiting on you to test it, not because it's yours to
                            build. Without a marker the two read as the same kind of row. */}
                        {user?.employeeId
                          && item.testerId === user.employeeId
                          && item.assigneeId !== user.employeeId && (
                          <StatusBadge tone="violet">
                            Testing · {item.testingDueDate ?? "no date"}
                          </StatusBadge>
                        )}

                        <StatusBadge tone={PRIORITY_TONE[item.priority]}>
                          {PRIORITY_LABELS[item.priority]}
                        </StatusBadge>

                        <span className="tnum w-10 text-right text-ink-500">
                          {formatHours(item.currentEstimateHours)}
                        </span>

                        <span
                          className={cn(
                            "tnum w-24 text-right",
                            item.committedDueDate == null
                              ? "text-ink-400"
                              : item.scheduleVarianceDays > 0
                                ? "font-medium text-red-600"
                                : "text-ink-500",
                          )}
                          title={
                            item.committedDueDate
                              ? formatSlippage(item.scheduleVarianceDays)
                              : undefined
                          }
                        >
                          {item.committedDueDate ?? "no date"}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </Card>
      )}
    </>
  );
}

/**
 * The one line that says how much of your own work is on screen.
 *
 * <p>When a project filter is on it names both numbers, because "3 open items" under a chip that
 * hides seventeen others is how somebody concludes the rest went missing.
 */
function subtitle(state: {
  shown: number;
  mine: number;
  filteredTo?: string;
  blocked: number;
}): string {
  const inProject = state.filteredTo ? ` in ${state.filteredTo}` : "";

  if (state.mine === 0) {
    return "Nothing is assigned to you.";
  }

  const noun = state.shown === 1 ? "item" : "items";
  const scope = state.filteredTo
    ? `${state.shown} of your ${state.mine} open items${inProject}`
    : `${state.shown} open ${noun}`;

  return `${scope}${state.blocked > 0 ? `, ${state.blocked} blocked` : ""}.`;
}

function ProjectChip({
  label,
  title,
  count,
  selected,
  onClick,
}: {
  label: string;
  title?: string;
  count?: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={selected}
      className={cn(
        "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
        /* brand-600, not 500: dark mode inverts 400/500/700 into light violets meant for text on
           a dark ground, so white on brand-500 measures 2.79:1 there. 600 is the one tier that
           holds its value in both themes, which is why every button in the app is built on it. */
        selected
          ? "border-brand-600 bg-brand-600 text-white"
          : "border-ink-200 bg-surface text-ink-700 hover:border-ink-300 hover:bg-ink-50",
      )}
    >
      {label}
      {count != null && (
        <span className={cn("ml-1.5 tnum", selected ? "text-white/75" : "text-ink-500")}>
          {count}
        </span>
      )}
    </button>
  );
}
